import debounce from "lodash.debounce";
import React from "react";
import { Dimensions, Platform, StyleSheet, Text, View } from "react-native";
import ContextBubblesTopList from "../../components/ContextBubblesTopList";
import IconBottomButton from "../../components/IconBottomButton";
import ItemsFlatList from "../../components/ItemsFlatList";
import SearchBar from "../../components/SearchBar";
import {
  loadSearchResultTasks,
  resetSearchResultTask,
} from "../../services/tasksService";
import cColors from "../../styles/componentColors";

const { width } = Dimensions.get("window");

interface SaveForLaterSelectPageProps {
  navigation: any;
  filterIndex: string;
  setFilterIndex: (index: string) => void;
  searchPhrase: string;
  setSearchPhrase: (phrase: string) => void;
  clicked: boolean;
  setClicked: (clicked: boolean) => void;
  isSearchResults: boolean;
  setIsSearchResults: (isSearchResults: boolean) => void;
  shouldUseAllOnNextSearch: boolean;
  setShouldUseAllOnNextSearch: (shouldUse: boolean) => void;
  setTaskIds: (taskIds: string[]) => void;
  done: () => void;
}

const SaveForLaterSelectPage: React.FC<SaveForLaterSelectPageProps> = ({
  navigation,
  filterIndex,
  setFilterIndex,
  searchPhrase,
  setSearchPhrase,
  clicked,
  setClicked,
  isSearchResults,
  setIsSearchResults,
  shouldUseAllOnNextSearch,
  setShouldUseAllOnNextSearch,
  setTaskIds,
  done,
}) => {
  const debouncedHandleSearchPhraseChange = React.useMemo(
    () =>
      debounce((phrase: string) => {
        if (phrase) {
          loadSearchResultTasks({
            search_term: phrase,
            context_id: filterIndex,
            task_type: "SAVE_FOR_LATER",
          });
        }
      }, 300),
    [filterIndex],
  );

  const handleSearchPhraseChange = (phrase: string) => {
    setSearchPhrase(phrase);
    if (phrase === "") {
      resetSearchResultTask();
    } else {
      setIsSearchResults(clicked);
    }
    debouncedHandleSearchPhraseChange.cancel();
    debouncedHandleSearchPhraseChange(phrase);
  };

  return (
    <View style={styles.page}>
      <View style={styles.headerContainer}>
        <Text style={styles.header}>Move saved tasks to today</Text>
        <SearchBar
          clicked={clicked}
          searchPhrase={searchPhrase}
          setSearchPhrase={handleSearchPhraseChange}
          setClicked={(clickedVal) => {
            if (shouldUseAllOnNextSearch) {
              setShouldUseAllOnNextSearch(false);
              setFilterIndex("all");
            }
            setClicked(clickedVal);
            setIsSearchResults(clickedVal);
          }}
          onCancel={() => {
            setShouldUseAllOnNextSearch(true);
          }}
        />
      </View>
      <ContextBubblesTopList
        setContextIndex={(index: string) => {
          setFilterIndex(index);
          setShouldUseAllOnNextSearch(index === "all");
        }}
        navigation={navigation}
        contextIndex={filterIndex}
        marginSide="left"
      />
      <ItemsFlatList
        hideArrowButton
        listType="SaveForLater"
        navigation={navigation}
        extraSpaceAtEnd
        filterIndex={filterIndex}
        setTaskIds={setTaskIds}
        isSearchResults={isSearchResults}
        paddingInCell
      />
      <IconBottomButton
        side="right"
        singleTap={done}
        text="🆗"
        isLandscape={false}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    paddingTop: 0,
    paddingBottom: 12,
    fontSize: 24,
    color: cColors.HEADER_TEXT_COLOR,
  },
  page: {
    backgroundColor: cColors.PAGE_BG_COLOR,
    flex: 1,
    width: Platform.OS === "web" ? "100%" : width,
    paddingTop: 0,
  },
  headerContainer: {
    paddingTop: 20,
    paddingBottom: 20,
    paddingHorizontal: 14,
  },
});

export default SaveForLaterSelectPage;
